import React from 'react';
import { useForm } from 'react-hook-form';
import { motion } from 'framer-motion';

interface GoalsStepProps {
  onNext: (data: { targetScore: number; subjectScores: Record<string, number> }) => void;
  onBack: () => void;
  examType: string;
  defaultValue?: { targetScore?: number; subjectScores?: Record<string, number> };
}

interface FormData {
  targetScore: number;
  subjectScores: Record<string, number>;
}

const GoalsStep: React.FC<GoalsStepProps> = ({ onNext, onBack, examType, defaultValue }) => {
  const { register, handleSubmit, formState: { errors } } = useForm<FormData>({
    defaultValues: {
      targetScore: defaultValue?.targetScore || 0,
      subjectScores: defaultValue?.subjectScores || {}
    }
  });

  const getMaxScore = () => {
    switch (examType.toLowerCase()) {
      case 'neet': return 720;
      case 'jee-mains': 
      case 'jee mains': return 300;
      case 'jee-advanced': 
      case 'jee advanced': return 372;
      default: return 1000;
    }
  };

  const getSubjects = () => {
    console.log('Current examType:', examType); // Debug log
    const examTypeLower = examType.toLowerCase();
    if (examTypeLower === 'neet') {
      return [
        { id: 'physics', name: 'Physics', maxScore: 180 },
        { id: 'chemistry', name: 'Chemistry', maxScore: 180 },
        { id: 'biology', name: 'Biology', maxScore: 360 }
      ];
    } else if (examTypeLower === 'jee-mains' || examTypeLower === 'jee mains') {
      return [
        { id: 'physics', name: 'Physics', maxScore: 100 },
        { id: 'chemistry', name: 'Chemistry', maxScore: 100 },
        { id: 'mathematics', name: 'Mathematics', maxScore: 100 }
      ];
    } else if (examTypeLower === 'jee-advanced' || examTypeLower === 'jee advanced') {
      return [
        { id: 'physics', name: 'Physics', maxScore: 124 },
        { id: 'chemistry', name: 'Chemistry', maxScore: 124 },
        { id: 'mathematics', name: 'Mathematics', maxScore: 124 }
      ];
    }
    return [];
  };

  const onSubmit = (data: FormData) => {
    onNext(data);
  };

  const maxScore = getMaxScore();
  const subjects = getSubjects();
  
  console.log('Subjects found:', subjects); // Debug log

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.3 }}
      className="step-content"
    >
      <div className="container-fluid py-4">
        <div className="row justify-content-center">
          <div className="col-lg-8">
            <div className="text-center mb-5">
              <h2 className="display-6 fw-bold text-primary mb-3">
                Set Your Goals
              </h2>
              <p className="lead text-muted">
                Define your target scores for both overall performance and individual subjects
              </p>
            </div>

            <form onSubmit={handleSubmit(onSubmit)}>
              {/* Total Score Goal */}
              <div className="card border mb-4 shadow-sm">
                <div className="card-header bg-white border-bottom">
                  <h5 className="mb-0 text-dark">
                    <i className="fas fa-bullseye text-primary me-2"></i>
                    Overall Target Score
                  </h5>
                </div>
                <div className="card-body bg-light">
                  <div className="row align-items-center">
                    <div className="col-md-6">
                      <label className="form-label fw-semibold text-dark">
                        Target Score for {examType.toUpperCase()}
                        <span className="text-muted ms-2">(Max: {maxScore})</span>
                      </label>
                      <input
                        type="number"
                        className={`form-control form-control-lg border-2 ${errors.targetScore ? 'is-invalid border-danger' : 'border-primary'}`}
                        placeholder={`Enter target score (1-${maxScore})`}
                        {...register('targetScore', {
                          required: 'Target score is required',
                          min: { value: 1, message: 'Score must be at least 1' },
                          max: { value: maxScore, message: `Score cannot exceed ${maxScore}` }
                        })}
                      />
                      {errors.targetScore && (
                        <div className="invalid-feedback">
                          {errors.targetScore.message}
                        </div>
                      )}
                    </div>
                    <div className="col-md-6">
                      <div className="text-muted">
                        <strong className="text-dark">Maximum Score:</strong> {maxScore}<br/>
                        <strong className="text-dark">Current Exam:</strong> {examType.toUpperCase()}<br/>
                        <small className="text-primary">
                          <i className="fas fa-lightbulb me-1"></i>
                          Set a realistic but challenging overall target
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Subject-wise Goals */}
              <div className="card border mb-4 shadow-sm">
                <div className="card-header bg-white border-bottom">
                  <h5 className="mb-0 text-dark">
                    <i className="fas fa-chart-bar text-success me-2"></i>
                    Subject-wise Target Scores
                  </h5>
                </div>
                <div className="card-body bg-light">
                  <div className="row g-4">
                    {subjects.length > 0 ? (
                      subjects.map((subject) => (
                        <div key={subject.id} className="col-md-4">
                          <label className="form-label fw-semibold text-dark">
                            {subject.name}
                            <span className="text-muted ms-2">(Max: {subject.maxScore})</span>
                          </label>
                          <input
                            type="number"
                            className={`form-control border-2 ${errors.subjectScores?.[subject.id] ? 'is-invalid border-danger' : 'border-success'}`}
                            placeholder={`Target for ${subject.name}`}
                            {...register(`subjectScores.${subject.id}`, {
                              required: `${subject.name} target score is required`,
                              min: { value: 1, message: 'Score must be at least 1' },
                              max: { value: subject.maxScore, message: `Score cannot exceed ${subject.maxScore}` }
                            })}
                          />
                          {errors.subjectScores?.[subject.id] && (
                            <div className="invalid-feedback">
                              {errors.subjectScores[subject.id]?.message}
                            </div>
                          )}
                          <small className="text-muted">
                            Maximum: {subject.maxScore} marks
                          </small>
                        </div>
                      ))
                    ) : (
                      <div className="col-12">
                        <div className="alert alert-warning border-warning">
                          <i className="fas fa-exclamation-triangle me-2 text-warning"></i>
                          <span className="text-dark">No subjects found for exam type: "{examType}". Please select an exam type first.</span>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="alert alert-light border mt-4 mb-0">
                    <div className="d-flex align-items-start">
                      <i className="fas fa-info-circle me-2 mt-1 text-primary"></i>
                      <div>
                        <strong className="text-dark">Pro Tips:</strong>
                        <ul className="mb-0 mt-2 text-dark">
                          <li>Your subject-wise goals should add up to or be close to your overall target score</li>
                          <li>Focus more on subjects where you have room for improvement</li>
                          <li>Set realistic but challenging goals to stay motivated</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="d-flex justify-content-between">
                <button
                  type="button"
                  onClick={onBack}
                  className="btn btn-outline-secondary btn-lg px-4"
                >
                  <i className="fas fa-arrow-left me-2"></i>
                  Back
                </button>
                
                <button
                  type="submit"
                  className="btn btn-primary btn-lg px-5"
                >
                  Continue
                  <i className="fas fa-arrow-right ms-2"></i>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default GoalsStep;

